import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api-client.js';
import { Input, Select } from './ui/input.js';

/**
 * Render the right input for an "Assigned to" value depending on the
 * picked kind. When the tenant has a Wildix Cloud adapter configured, we
 * fetch the live directory and render a dropdown of real colleagues
 * (kind='extension') or call queues (kind='callgroup'). Otherwise we
 * gracefully fall back to a free-text Input so the on-prem connector
 * path keeps working.
 *
 * - 404 from /v1/admin/wildix/* (no cloud adapter) → fallback to text
 * - 502 (upstream Wildix error) → fallback to text + visible warning
 * - Loading → text input is shown so the form is never blocked
 */
export interface AssigneePickerProps {
  kind: '' | 'extension' | 'callgroup';
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}

interface DirectoryEntry {
  extension: string;
  name: string;
  email?: string;
}

export function AssigneePicker(props: AssigneePickerProps): JSX.Element {
  const { kind, value, onChange, disabled } = props;

  const path =
    kind === 'extension'
      ? '/v1/admin/wildix/colleagues'
      : kind === 'callgroup'
        ? '/v1/admin/wildix/call-queues'
        : null;

  const dirQ = useQuery({
    queryKey: ['wildix-directory', kind],
    queryFn: () => api.get(path!) as Promise<{ data: DirectoryEntry[] }>,
    enabled: !!path,
    retry: (failureCount, err) => {
      // Don't retry 404 (no cloud adapter) — fallback to text input.
      if (err instanceof ApiError && (err.status === 404 || err.status === 401)) return false;
      return failureCount < 2;
    },
    staleTime: 5 * 60_000,
  });

  if (!kind) {
    return (
      <Input value={value} disabled placeholder="Disabled until a kind is selected" />
    );
  }

  const noCloud =
    dirQ.error instanceof ApiError && dirQ.error.status === 404;
  const upstreamErr =
    dirQ.error instanceof ApiError && dirQ.error.status === 502;

  // Show text input fallback when:
  //   - tenant has no cloud adapter (404), OR
  //   - cloud reachable but Wildix upstream errored (502), OR
  //   - still loading (don't block the form), OR
  //   - we got data but it's empty (let the user type the value they know)
  const entries = dirQ.data?.data ?? [];
  const showText = noCloud || upstreamErr || dirQ.isLoading || entries.length === 0;

  if (showText) {
    return (
      <div className="space-y-1">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={
            kind === 'callgroup'
              ? 'Queue id, e.g. Q1234'
              : 'Internal extension, e.g. 101'
          }
          disabled={disabled}
        />
        {upstreamErr && (
          <p className="text-xs text-warning">
            Wildix Cloud directory unavailable right now — using free-text entry.
          </p>
        )}
        {dirQ.isLoading && (
          <p className="text-xs text-ink-muted">Checking Wildix directory…</p>
        )}
      </div>
    );
  }

  // Live dropdown from the Wildix Cloud adapter.
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      <option value="">— Select {kind === 'callgroup' ? 'queue' : 'extension'} —</option>
      {entries.map((e) => (
        <option key={e.extension} value={e.extension}>
          {e.name} ({e.extension})
        </option>
      ))}
      {/* Keep a free-form fallback when the saved value isn't in the directory anymore. */}
      {value && !entries.some((e) => e.extension === value) && (
        <option value={value}>(legacy) {value}</option>
      )}
    </Select>
  );
}
